import http from "@/configs/AxiosConfig";
import store from "../store/index";

const api = http;

/**
 * LogService — Serviço centralizado de logs do sistema Rifa365.
 */
class LogService {
  static logCache = new Map();
  static ANTI_SPAM_MS = 2000;

  static _shouldLog(key) {
    const now = Date.now();
    const last = this.logCache.get(key);
    if (!last || now - last > this.ANTI_SPAM_MS) {
      this.logCache.set(key, now);
      return true;
    }
    return false;
  }

  static async log(module, action, message, options = {}) {
    try {
      const user = store.getters["user"];
      if (!user?.id) return;

      const description = `[${module.toUpperCase()}] [${action.toUpperCase()}] ${message}`;
      if (!this._shouldLog(`${user.id}::${description}`)) return;

      const payload = {
        description,
        module: module.toUpperCase(),
        action: action.toUpperCase(),
        user_agent: navigator.userAgent,
        severity: options.severity || "info",
        entity_type: options.entityType || null,
        entity_id: options.entityId ? String(options.entityId) : null,
        metadata: options.metadata ? JSON.stringify(options.metadata) : null,
        route_path: options.routePath || window.location.pathname,
      };

      await api.post("/logs", payload);
    } catch (err) {
      if (err.response?.status !== 401) {
        console.warn("[LogService] Falha ao registrar log:", err.message);
      }
    }
  }

  static async getLogs(filters = {}, page = 1, perPage = 50) {
    const response = await api.get("/logs", {
      params: { page, limit: perPage, ...filters },
    });
    return response.data;
  }

  static async getLogById(id) {
    const response = await api.get(`/logs/${id}`);
    return response.data;
  }

  static logLogin(userEmail) {
    return this.log("AUTH", "LOGIN", `Usuário ${userEmail} fez login no sistema`);
  }

  static logLogout(userEmail = "") {
    return this.log("AUTH", "LOGOUT", `Usuário ${userEmail || "desconhecido"} encerrou a sessão`);
  }

  static logPasswordChange() {
    return this.log("AUTH", "AÇÃO", "Usuário alterou a própria senha");
  }

  static logCampaignCreated(campaignName) {
    return this.log("CAMPANHAS", "CRIAR", `Usuário criou a campanha "${campaignName}"`);
  }

  static logCampaignEdited(campaignName) {
    return this.log("CAMPANHAS", "EDITAR", `Usuário editou a campanha "${campaignName}"`);
  }

  static logCampaignActivated(campaignName) {
    return this.log("CAMPANHAS", "ATIVAR", `Usuário ativou a campanha "${campaignName}"`);
  }

  static logCampaignDeactivated(campaignName) {
    return this.log("CAMPANHAS", "DESATIVAR", `Usuário desativou a campanha "${campaignName}"`);
  }

  static logCampaignDeleted(campaignName) {
    return this.log("CAMPANHAS", "DELETAR", `Usuário deletou a campanha "${campaignName}"`);
  }

  static logCampaignImageAdded(campaignName) {
    return this.log("CAMPANHAS", "AÇÃO", `Usuário adicionou imagem à campanha "${campaignName}"`);
  }

  static logCampaignImageRemoved(campaignName) {
    return this.log("CAMPANHAS", "AÇÃO", `Usuário removeu imagem da campanha "${campaignName}"`);
  }

  static logPrizesConfigured(campaignName) {
    return this.log("CAMPANHAS", "AÇÃO", `Usuário configurou os prêmios da campanha "${campaignName}"`);
  }

  static logPromotionCreated(campaignName, promotionType) {
    return this.log("CAMPANHAS", "AÇÃO", `Usuário criou promoção do tipo "${promotionType}" na campanha "${campaignName}"`);
  }

  static logMarkedAsPaid(participantName, campaignName) {
    return this.log("PEDIDOS", "AÇÃO", `Usuário marcou o pedido de "${participantName}" como pago na campanha "${campaignName}"`);
  }

  static logOrderCanceled(participantName, campaignName) {
    return this.log("PEDIDOS", "AÇÃO", `Usuário cancelou o pedido de "${participantName}" na campanha "${campaignName}"`);
  }

  static logPaymentConfirmed(campaignName, value) {
    return this.log("PEDIDOS", "AÇÃO", `Pagamento confirmado na campanha "${campaignName}" — Valor: R$ ${value}`);
  }

  static logOrdersViewed(campaignName) {
    return this.log("PEDIDOS", "AÇÃO", `Usuário visualizou os pedidos da campanha "${campaignName}"`);
  }

  static logOrdersExported(campaignName, format) {
    return this.log("PEDIDOS", "AÇÃO", `Usuário exportou os pedidos da campanha "${campaignName}" em formato ${format.toUpperCase()}`);
  }

  static logUserBlocked(targetEmail) {
    return this.log("USUÁRIOS", "AÇÃO", `Usuário bloqueou a conta de "${targetEmail}"`);
  }

  static logUserUnblocked(targetEmail) {
    return this.log("USUÁRIOS", "AÇÃO", `Usuário desbloqueou a conta de "${targetEmail}"`);
  }

  static logUserRoleChanged(targetEmail, newRole) {
    return this.log("USUÁRIOS", "AÇÃO", `Usuário alterou a função de "${targetEmail}" para ${newRole}`);
  }

  static logWinnersSet(campaignName, count) {
    return this.log("USUÁRIOS", "AÇÃO", `Usuário definiu ${count} ganhador(es) para a campanha "${campaignName}"`);
  }

  static logWithdrawalRequested(amount) {
    return this.log("FINANCEIRO", "AÇÃO", `Usuário solicitou saque de R$ ${amount}`);
  }

  static logWithdrawalApproved(targetEmail, amount) {
    return this.log("FINANCEIRO", "AÇÃO", `Saque de R$ ${amount} aprovado para "${targetEmail}"`);
  }

  static logWithdrawalRejected(targetEmail, amount) {
    return this.log("FINANCEIRO", "AÇÃO", `Saque de R$ ${amount} rejeitado para "${targetEmail}"`);
  }

  static logProfileUpdated() {
    return this.log("CONFIGURAÇÕES", "AÇÃO", "Usuário atualizou os dados do perfil");
  }

  static logProfilePhotoChanged() {
    return this.log("CONFIGURAÇÕES", "AÇÃO", "Usuário alterou a foto de perfil");
  }

  static logPaymentProviderConfigured(providerName) {
    return this.log("CONFIGURAÇÕES", "AÇÃO", `Usuário configurou o provedor de pagamento "${providerName}"`);
  }

  static logSystemSettingChanged(settingName) {
    return this.log("CONFIGURAÇÕES", "AÇÃO", `Usuário alterou a configuração "${settingName}"`);
  }

  static logError(errorMessage, context = "") {
    const ctx = context ? ` [contexto: ${context}]` : "";
    return this.log("SISTEMA", "ERRO", `Erro: ${errorMessage}${ctx}`, { severity: "error" });
  }

  static logAffiliateCreated(affiliateName) {
    return this.log("SISTEMA", "AÇÃO", `Usuário "${affiliateName}" se tornou afiliado`);
  }

  static logCouponCreated(couponCode) {
    return this.log("SISTEMA", "AÇÃO", `Usuário criou o cupom de desconto "${couponCode}"`);
  }

  static logCouponDeleted(couponCode) {
    return this.log("SISTEMA", "AÇÃO", `Usuário deletou o cupom "${couponCode}"`);
  }
}

export default LogService;
